import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plus, Loader2, AlertTriangle, CheckCircle, XCircle, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import { orgsApi, type Organization, CONTRACT_BADGE, CONTRACT_LABEL } from '../api/organizations';
import { fmtTime, PROJECT_LABEL, PROJECT_BADGE } from '../lib/format';
import { api } from '../api/client';

type OrgSortKey = 'name' | 'inn' | 'project' | 'contract' | 'tariff'
                | 'clients' | 'open' | 'closed' | 'contact' | 'created' | 'last_ticket';

const PROJECT_ORDER = { support: 0, pilot: 1, no_support: 2 } as const;
const CONTRACT_ORDER = { active: 0, expiring_soon: 1, expired: 2, inactive: 3 } as const;

function orgValue(o: Organization, k: OrgSortKey): string | number {
  switch (k) {
    case 'name':        return o.name.toLowerCase();
    case 'inn':         return o.inn;
    case 'project':     return PROJECT_ORDER[o.project_status] ?? 99;
    case 'contract':    return CONTRACT_ORDER[o.contract_status] ?? 99;
    case 'tariff':      return (o.tariff || '').toLowerCase();
    case 'clients':     return o.clients_count ?? 0;
    case 'open':        return o.tickets_open_count ?? 0;
    case 'closed':      return o.tickets_closed_count ?? 0;
    case 'contact':     return (o.contact_person || '').toLowerCase();
    case 'created':     return o.created_at || '';
    case 'last_ticket': return o.last_ticket_at || '';
  }
}

type OrgFilters = {
  q: string;
  projectStatus: '' | 'support' | 'pilot' | 'no_support';
  contractActive: '' | 'true' | 'false';
  showHidden: boolean;
};

type OrgSavedView = { id: string; name: string; filters: OrgFilters; sort: any };

const ORGS_VIEWS_KEY = 'orgs.savedViews';

function loadOrgsViews(): OrgSavedView[] {
  try { return JSON.parse(localStorage.getItem(ORGS_VIEWS_KEY) || '[]'); }
  catch { return []; }
}

function saveOrgsView(v: OrgSavedView) {
  const all = loadOrgsViews().filter(x => x.id !== v.id);
  all.push(v);
  localStorage.setItem(ORGS_VIEWS_KEY, JSON.stringify(all));
}

function deleteOrgsView(id: string) {
  localStorage.setItem(ORGS_VIEWS_KEY,
    JSON.stringify(loadOrgsViews().filter(x => x.id !== id)));
}

export default function OrganizationsPage() {
  const [items, setItems] = useState<Organization[]>([]);
  const [filters, setFilters] = useState<OrgFilters>(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      q: sp.get('q') || '',
      projectStatus: (sp.get('project_status') as any) || '',
      contractActive: (sp.get('contract_active') as any) || '',
      showHidden: sp.get('hidden') === '1',
    };
  });
  const q = filters.q;
  const setQ = (v: string) => setFilters(f => ({ ...f, q: v }));
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [views, setViews] = useState<OrgSavedView[]>(loadOrgsViews());

  const [sort, setSort] = useState<{ k: OrgSortKey; dir: 'asc'|'desc' } | null>(
    { k: 'name', dir: 'asc' }
  );
  const filtered = useMemo(() => {
    let xs = items;
    if (!filters.showHidden) xs = xs.filter(o => !o.is_hidden);
    if (filters.projectStatus) xs = xs.filter(o => o.project_status === filters.projectStatus);
    if (filters.contractActive === 'true') xs = xs.filter(o => o.contract_active === true);
    if (filters.contractActive === 'false') xs = xs.filter(o => o.contract_active === false);
    return xs;
  }, [items, filters.showHidden, filters.projectStatus, filters.contractActive]);

  const sorted = useMemo(() => {
    if (!sort) return filtered;
    const { k, dir } = sort;
    return [...filtered].sort((a, b) => {
      const va = orgValue(a, k), vb = orgValue(b, k);
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sort]);
  const toggleSort = (k: OrgSortKey) => setSort(s => {
    if (!s || s.k !== k) return { k, dir: 'asc' };
    if (s.dir === 'asc')  return { k, dir: 'desc' };
    return null;
  });

  const load = async () => {
    setLoading(true);
    try { setItems(await orgsApi.list(q || undefined)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Building2 size={20} className="text-blue-400" /> Организации
        </h2>
        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Поиск по имени/ИНН…"
          className="bg-slate-900 border border-slate-700 rounded-full py-1.5 px-4 text-sm text-white focus:border-blue-500 outline-none w-72" />
        <select value={filters.projectStatus}
                onChange={e => setFilters(f => ({ ...f, projectStatus: e.target.value as any }))}
                className="bg-slate-900 border border-slate-700 rounded-full py-1.5 px-3 text-xs text-white">
          <option value="">Статус: все</option>
          <option value="support">Поддержка</option>
          <option value="pilot">Пилот</option>
          <option value="no_support">Без поддержки</option>
        </select>
        <select value={filters.contractActive}
                onChange={e => setFilters(f => ({ ...f, contractActive: e.target.value as any }))}
                className="bg-slate-900 border border-slate-700 rounded-full py-1.5 px-3 text-xs text-white">
          <option value="">Договор: любой</option>
          <option value="true">активен</option>
          <option value="false">истёк / не активен</option>
        </select>
        <label className="text-xs text-slate-300 flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={filters.showHidden}
                 onChange={e => setFilters(f => ({ ...f, showHidden: e.target.checked }))}
                 className="accent-blue-600" />
          скрытые
        </label>
        <button onClick={async () => {
          try {
            const { data } = await api.post('/api/v1/organizations/_battlecard-sync');
            alert(`Battlecard sync:\n  получено: ${data.fetched}\n  создано: ${data.created}\n  обновлено: ${data.updated}\n  пропущено: ${data.skipped}`);
            load();
          } catch (e: any) {
            alert(e?.response?.data?.detail || e?.message || 'Ошибка');
          }
        }}
          className="ml-auto text-xs inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-2 rounded"
          title="Подтянуть Customer'ов из Battlecard">
          ↻ BC sync
        </button>
        <button onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-4 py-2 rounded">
          <Plus size={14} /> Добавить
        </button>
      </div>

      {/* Saved-views: pill-row */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3 text-[11px]">
        {views.map(v => (
          <span key={v.id}
                onClick={() => { setFilters(v.filters); setSort(v.sort); }}
                className="cursor-pointer inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-slate-200 px-2 py-1 rounded-full">
            {v.name}
            <button onClick={(e) => { e.stopPropagation(); deleteOrgsView(v.id); setViews(loadOrgsViews()); }}
                    className="text-slate-500 hover:text-red-400" title="Удалить view">×</button>
          </span>
        ))}
        <button onClick={() => {
                  const name = window.prompt('Имя view:');
                  if (!name) return;
                  const v: OrgSavedView = { id: 'u_' + Date.now(), name, filters, sort };
                  saveOrgsView(v); setViews(loadOrgsViews());
                }}
                className="text-[11px] text-slate-400 hover:text-white border border-dashed border-slate-700 rounded-full px-2 py-1">
          + сохранить вид
        </button>
        <span className="ml-auto text-[10px] text-slate-500">
          показано {sorted.length} из {items.length}
        </span>
      </div>

      {loading ? <Loader2 className="animate-spin text-blue-400 mx-auto mt-10" /> : (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/50 text-[10px] text-slate-500 uppercase tracking-wider">
              <tr>
                <OrgTh label="Название"  k="name"        sort={sort} onClick={toggleSort} />
                <OrgTh label="ИНН / КПП" k="inn"         sort={sort} onClick={toggleSort} />
                <OrgTh label="Статус"    k="project"     sort={sort} onClick={toggleSort} />
                <OrgTh label="Договор"   k="contract"    sort={sort} onClick={toggleSort} />
                <OrgTh label="Тариф"     k="tariff"      sort={sort} onClick={toggleSort} />
                <OrgTh label="Клиенты"   k="clients"     sort={sort} onClick={toggleSort} align="center" />
                <OrgTh label="Открыто"   k="open"        sort={sort} onClick={toggleSort} align="center" />
                <OrgTh label="Закрыто"   k="closed"      sort={sort} onClick={toggleSort} align="center" />
                <OrgTh label="Контакт"   k="contact"     sort={sort} onClick={toggleSort} />
                <OrgTh label="Посл. тикет" k="last_ticket" sort={sort} onClick={toggleSort} align="right" />
                <OrgTh label="Создана"   k="created"     sort={sort} onClick={toggleSort} align="right" />
                <th className="text-right py-3 px-4 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr><td colSpan={12} className="py-10 text-center text-slate-500">Нет организаций</td></tr>
              ) : sorted.map(o => (
                <tr key={o.id} className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="py-3 px-4">
                    <Link to={`/organizations/${o.id}`} className="text-white hover:text-blue-400 font-medium">{o.name}</Link>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs text-slate-400">
                    {o.inn}{o.kpp && <span className="text-slate-600"> / {o.kpp}</span>}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold border ${PROJECT_BADGE[o.project_status]}`}>
                      {PROJECT_LABEL[o.project_status]}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <ContractBadge o={o} />
                  </td>
                  <td className="py-3 px-4 text-slate-400">{o.tariff || <span className="text-slate-600">—</span>}</td>
                  <td className="py-3 px-2 text-center text-xs text-slate-300">{o.clients_count ?? 0}</td>
                  <td className="py-3 px-2 text-center text-xs">
                    <span className={(o.tickets_open_count ?? 0) > 0 ? 'text-yellow-400 font-bold' : 'text-slate-600'}>
                      {o.tickets_open_count ?? 0}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-center text-xs text-slate-500">{o.tickets_closed_count ?? 0}</td>
                  <td className="py-3 px-4 text-slate-400 text-xs">
                    {o.contact_person && <div>{o.contact_person}</div>}
                    {o.contact_phone && <div className="text-slate-600 font-mono">{o.contact_phone}</div>}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-500 text-xs whitespace-nowrap">
                    {o.last_ticket_at ? fmtTime(o.last_ticket_at) : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-500 text-xs whitespace-nowrap">{fmtTime(o.created_at)}</td>
                  <td className="py-3 px-2 text-right">
                    <button onClick={async (e) => {
                      e.preventDefault(); e.stopPropagation();
                      if (!window.confirm(`Перенести «${o.name}» в корзину? Можно будет восстановить из Настройки → Корзина.`)) return;
                      try { await orgsApi.remove(o.id); load(); }
                      catch (e: any) { alert(e?.response?.data?.detail || 'Ошибка'); }
                    }} title="В корзину"
                       className="text-slate-500 hover:text-red-400 p-1">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && <CreateModal onClose={() => setCreateOpen(false)} onCreated={() => { load(); setCreateOpen(false); }} />}
    </div>
  );
}

const OrgTh: React.FC<{
  label: string; k: OrgSortKey;
  sort: { k: OrgSortKey; dir: 'asc'|'desc' } | null;
  onClick: (k: OrgSortKey) => void;
  align?: 'left'|'center'|'right';
}> = ({ label, k, sort, onClick, align }) => {
  const active = sort?.k === k;
  const dir = sort?.dir;
  const al = align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left';
  return (
    <th className={`py-3 px-4 cursor-pointer select-none ${al} ${active ? 'text-blue-400' : 'hover:text-slate-300'}`}
        onClick={() => onClick(k)}>
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  );
};

export function ContractBadge({ o }: { o: { contract_status: string; contract_days_left?: number | null; contract_no?: string | null } }) {
  const status = (o.contract_status || 'active') as keyof typeof CONTRACT_BADGE;
  const Icon = status === 'active' ? CheckCircle
              : status === 'expiring_soon' ? AlertTriangle
              : status === 'expired' ? XCircle : XCircle;
  // Краткий лейбл: для expiring_soon показываем «Истекает 12 дн»; № договора — в title.
  const label = status === 'expiring_soon' && o.contract_days_left != null
    ? `Истекает ${o.contract_days_left} дн`
    : CONTRACT_LABEL[status];
  return (
    <span title={o.contract_no ? `Договор № ${o.contract_no}` : undefined}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border whitespace-nowrap ${CONTRACT_BADGE[status]}`}>
      <Icon size={11} />
      {label}
    </span>
  );
}

function CreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '', inn: '', kpp: '', legal_address: '',
    contact_person: '', contact_position: '',
    contact_phone: '', contact_phone_ext: '',
    contact_email: '', contact_telegram: '', contact_max: '',
    telegram_chat_url: '', max_chat_url: '',
    tariff: '', contract_no: '', contract_active: true,
    contract_started_at: '', contract_ends_at: '', notes: '',
    project_status: 'support' as 'support' | 'pilot' | 'no_support',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upd = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      const payload: any = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });
      await orgsApi.create(payload);
      onCreated();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10">
      <form onSubmit={submit} className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
        <div className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Новая организация</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">✕</button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          <Field label="Название *"><Inp v={form.name} k="name" upd={upd} required /></Field>
          <Field label="ИНН *">
            <div className="flex gap-1">
              <Inp v={form.inn} k="inn" upd={upd} required />
              <button type="button" onClick={async () => {
                if (!form.inn) { alert('Введите ИНН'); return; }
                try {
                  const { fnsApi } = await import('../api/v2');
                  const r = await fnsApi.lookup(form.inn);
                  if (r.name) upd('name', r.name);
                  if (r.kpp) upd('kpp', r.kpp);
                  if (r.legal_address) upd('legal_address', r.legal_address);
                  if (r.full_name && !form.notes) upd('notes', r.full_name);
                  alert(`Заполнено по ИНН: ${r.name}`);
                } catch (e: any) {
                  alert(e?.response?.data?.detail || 'Не удалось получить данные по ИНН');
                }
              }} title="Заполнить по ИНН из ЕГРЮЛ"
                className="text-xs bg-blue-600 hover:bg-blue-500 text-white px-3 rounded whitespace-nowrap">
                ↺ ФНС
              </button>
            </div>
          </Field>
          <Field label="КПП"><Inp v={form.kpp} k="kpp" upd={upd} /></Field>
          <Field label="Тариф"><Inp v={form.tariff} k="tariff" upd={upd} /></Field>
          <Field label="Статус проекта">
            <select value={form.project_status} onChange={e => upd('project_status', e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none">
              <option value="support">{PROJECT_LABEL.support}</option>
              <option value="pilot">{PROJECT_LABEL.pilot}</option>
              <option value="no_support">{PROJECT_LABEL.no_support}</option>
            </select>
          </Field>
          <Field label="Контактное лицо"><Inp v={form.contact_person} k="contact_person" upd={upd} /></Field>
          <Field label="Должность"><Inp v={form.contact_position} k="contact_position" upd={upd} /></Field>
          <Field label="Телефон"><Inp v={form.contact_phone} k="contact_phone" upd={upd} placeholder="+7 495 ..." /></Field>
          <Field label="Доб."><Inp v={form.contact_phone_ext} k="contact_phone_ext" upd={upd} placeholder="402" /></Field>
          <Field label="Email"><Inp v={form.contact_email} k="contact_email" upd={upd} type="email" /></Field>
          <Field label="Telegram (ник)"><Inp v={form.contact_telegram} k="contact_telegram" upd={upd} placeholder="@username" /></Field>
          <Field label="MAX (ник)"><Inp v={form.contact_max} k="contact_max" upd={upd} placeholder="@username" /></Field>
          <span /> {/* spacer для grid-выравнивания */}
          <Field label="Ссылка на чат в Telegram" colSpan={2}><Inp v={form.telegram_chat_url} k="telegram_chat_url" upd={upd} placeholder="https://t.me/..." /></Field>
          <Field label="Ссылка на чат в MAX" colSpan={2}><Inp v={form.max_chat_url} k="max_chat_url" upd={upd} placeholder="https://max.ru/..." /></Field>
          <Field label="№ договора"><Inp v={form.contract_no} k="contract_no" upd={upd} /></Field>
          <Field label="Активен"><label className="flex items-center gap-2 mt-2 text-sm text-slate-300"><input type="checkbox" checked={form.contract_active} onChange={e => upd('contract_active', e.target.checked)} className="accent-blue-500" /> Договор действует</label></Field>
          <Field label="Действует с"><Inp v={form.contract_started_at} k="contract_started_at" upd={upd} type="date" /></Field>
          <Field label="Действует до"><Inp v={form.contract_ends_at} k="contract_ends_at" upd={upd} type="date" /></Field>
          <Field label="Юр. адрес" colSpan={2}><Inp v={form.legal_address} k="legal_address" upd={upd} /></Field>
          <Field label="Заметки" colSpan={2}>
            <textarea value={form.notes} onChange={e => upd('notes', e.target.value)}
              rows={2} className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:border-blue-500 outline-none" />
          </Field>
        </div>
        {err && <div className="bg-red-900/30 border-y border-red-800 text-red-300 px-4 py-2 text-sm">{err}</div>}
        <div className="p-4 flex justify-end gap-2 border-t border-slate-800">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
          <button type="submit" disabled={submitting || !form.name || !form.inn}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold">
            {submitting ? 'Создаю…' : 'Создать'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, colSpan }: any) {
  return (
    <label className={`block ${colSpan === 2 ? 'col-span-2' : ''}`}>
      <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">{label}</span>
      {children}
    </label>
  );
}
function Inp({ v, k, upd, ...rest }: any) {
  return <input value={v} onChange={e => upd(k, e.target.value)}
    className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" {...rest} />;
}
